import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import HeroSearch from './HeroSearch';
// The background: a generated golden-hour Riyadh skyline (KAFD and Kingdom
// Centre over new residential blocks), made for Safaqat 24, so it carries no
// third-party licence or credit. Built and contrast-gated by
// `node scripts/prep-hero-golden.mjs <render> --darken=0.3` (white 4.69:1
// against the 95th-percentile sky of the headline area).
import heroPhoto from '../../../assets/safaqat-hero-riyadh-golden.webp';

/** A licence credit to print on the photo. config/photoCredits' PhotoCredit fits it. */
export interface HeroImageCredit {
  author: string;
  sourceUrl: string;
  licence: string;
  licenceUrl: string;
  /** Our file is an adaptation (cropped, resized or colour-adjusted). */
  edited?: boolean;
}

export interface HeroImage {
  /** Bundled URL of a 1920x1080 WebP. */
  src: string;
  /** CSS object-position: which part of the photo survives each screen's crop. */
  objectPosition?: string;
  /**
   * Flat, neutral darkening over the whole photo, 0 to 1 (never a gradient). Only
   * for a photo whose sky is too bright for white type. Measure it, do not guess:
   * `node scripts/prep-hero-golden.mjs <file> --dry-run --veil=<value>`.
   */
  veil?: number;
  /** The licence credit. Leave it out for an image that needs none: no credit line renders. */
  credit?: HeroImageCredit;
}

/**
 * THE HERO IMAGE. The one place to change it.
 *
 * Replacing it: build the new file with `node scripts/prep-hero-golden.mjs <source>`
 * (it refuses to save unless white type reads over the sky, and can bake in a
 * neutral `--darken`), point the `heroPhoto` import above at it, and retune
 * `objectPosition`. A licensed photograph also needs `credit` (and its entry in
 * src/config/photoCredits.ts); a photo that measures short of the contrast bar
 * can take a flat `veil` instead of a re-bake.
 *
 * Today's image is generated for us, so there is no credit line. Its darkening
 * is baked in (`--darken=0.3`), and since the hero became a short band it also
 * takes a measured flat veil (see `veil` below).
 */
const HERO_IMAGE: HeroImage = {
  src: heroPhoto,
  // A little left of centre, so a phone-width crop keeps the KAFD towers in view.
  // 35% down: the headline sits on the sky and the city shows around the search
  // panel, while the text stays inside the top 45% of the frame, the band
  // prep-hero-golden.mjs darkens and measures (white 4.69:1).
  objectPosition: '45% 35%',
  // Measured (2026-10-07): on a phone the hero shows the whole frame, so the
  // subtitle lands on the city lights, white 3.20:1 there. A flat 20% veil lifts
  // it to 4.74:1 (WCAG 1.4.3 needs 4.5:1); the headline was already above 4:1.
  veil: 0.2,
};

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

/** The licence credit, small, in the corner, on a dark chip so it reads over any part of the photo. */
function HeroCredit({ credit }: { credit: HeroImageCredit }) {
  const { t } = useTranslation('hero');
  const fromCommons = /(^|\.)wikimedia\.org$/i.test(hostOf(credit.sourceUrl));
  const link =
    'text-white underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-white';
  return (
    <p
      data-hero-credit=""
      className="absolute start-3 top-3 z-10 m-0 max-w-[calc(100%-4.5rem)] rounded-md bg-black/65 px-2 py-1 text-xs leading-snug text-white"
    >
      {t('credit.photo')}{' '}
      <a href={credit.sourceUrl} target="_blank" rel="noopener noreferrer" className={link}>
        <bdi>{credit.author}</bdi> <span className="sr-only">{t('credit.newTab')}</span>
      </a>
      {fromCommons && (
        <>
          {t('credit.separator')}
          {t('credit.commons')}
        </>
      )}
      {t('credit.separator')}
      <a href={credit.licenceUrl} target="_blank" rel="noopener noreferrer" className={link}>
        <bdi>{credit.licence}</bdi> <span className="sr-only">{t('credit.newTab')}</span>
      </a>
      {credit.edited && <> {t('credit.edited')}</>}
    </p>
  );
}

/**
 * Home hero, in the manner of rightmove.co.uk's (owner, 2026-10-08): the skyline
 * in a rounded box nearly the full width of the screen; on it a white headline with
 * one phrase in the logo's yellow («أحلى صفقة»), a short subtitle, and the
 * deep-teal search panel centred under them. See Apps/web/THEME.md §8.
 *
 * Owner, 2026-10-07: first the full-screen photo read like a resort, then the
 * full-width band was too wide; so: a box, and the search inside it.
 *
 * - The type sits straight on the photo, so the photo must be dark enough under
 *   it. That is MEASURED, not estimated: scripts/prep-hero-golden.mjs gates the
 *   headline area at 3:1 (large headline) and 4.5:1 (subtitle), and
 *   HERO_IMAGE.veil tops up a photo that falls short. No gradient, no glass, no glow.
 * - The headline is still. It used to rotate through seven sayings, and moving
 *   text needs a pause control (WCAG 2.2.2); the owner did not want the button,
 *   so the headline stopped. HeroSayings stays in the tree for that choice.
 * - Screen readers get one static <h1> (hero.srHeadline) and the subtitle.
 * - The box's own colour (deep teal) keeps the white type readable before the
 *   photo has loaded.
 */
export default function HomeHero({ image = HERO_IMAGE }: { image?: HeroImage }) {
  const { t } = useTranslation('hero');
  const reduce = useReducedMotion() ?? false;

  return (
    <section className="relative w-full" aria-labelledby="hero-heading">
      {/* Nearly the full width of the screen, with small margins so the rounded
          corners show (owner, 2026-10-08, marked on a screenshot). */}
      <div className="mx-auto max-w-[120rem] px-3 pt-3 sm:px-4 sm:pt-4 lg:px-5 short:pt-3">
        <div className="relative overflow-hidden rounded-[20px] bg-[#002A23] sm:rounded-[28px]">
          <img
            src={image.src}
            alt=""
            aria-hidden="true"
            width={1920}
            height={1080}
            decoding="async"
            loading="eager"
            // React 18 only forwards the lowercase HTML attribute (camelCase warns).
            {...{ fetchpriority: 'high' }}
            className="absolute inset-0 h-full w-full object-cover"
            style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
          />
          {image.veil ? (
            <div
              data-hero-veil=""
              className="absolute inset-0"
              style={{ backgroundColor: `rgba(0, 0, 0, ${image.veil})` }}
              aria-hidden="true"
            />
          ) : null}

          {/* Short windows (tailwind.config `short` / `tiny`): less air, a smaller
              headline, and on the shortest no subtitle, so the hero never fills
              the screen on a scaled laptop or a phone on its side. */}
          <div className="relative z-10 px-3 pb-6 pt-9 sm:px-8 sm:pb-12 sm:pt-12 lg:px-12 lg:pb-14 lg:pt-14 xl:px-16 short:pb-7 short:pt-7">
            <div className="mx-auto max-w-3xl text-center text-white">
              <h1 id="hero-heading" className="sr-only">
                {t('srHeadline')}
              </h1>
              {/* One phrase in the logo's yellow, as rightmove colours "believe".
                  Gold may write on deep teal and on this dark sky (THEME.md §2.1). */}
              <p
                aria-hidden="true"
                data-hero-headline=""
                className="m-0 text-balance text-[1.75rem] font-bold leading-[1.3] sm:text-[2.25rem] md:text-[2.75rem] short:text-[2.125rem]"
              >
                {t('headlineLead')} <span className="text-[#FFC107]">{t('headlineAccent')}</span>
                {t('headlineTail') ? ` ${t('headlineTail')}` : ''}
              </p>
              <p className="mx-auto mb-0 mt-2 max-w-2xl text-balance text-[1.0625rem] font-medium leading-relaxed text-white sm:text-[1.25rem] short:text-[1.0625rem] tiny:hidden">
                {t('subtitle')}
              </p>
            </div>

            {/* The search panel across the photo, leaving a strip of it at each side
                (owner, 2026-10-08: as wide as marked on the screenshot). */}
            <motion.div
              className="mx-auto mt-6 w-full max-w-[100rem] sm:mt-8 short:mt-5"
              initial={reduce ? false : { opacity: 0, y: 18 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroSearch />
            </motion.div>
          </div>

          {image.credit && <HeroCredit credit={image.credit} />}
        </div>
      </div>
    </section>
  );
}
